import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  IconArrowRight,
  IconBrandWhatsapp,
  IconCash,
  IconCheck,
  IconChevronLeft,
  IconChevronRight,
  IconCircleCheck,
  IconCircleX,
  IconClock,
  IconCopy,
  IconDownload,
  IconHourglass,
  IconLock,
  IconMessage,
  IconPhone,
  IconPrinter,
  IconQrcode,
  IconSearch,
  IconShare,
  IconSparkles,
  IconTrophy,
  IconUsers,
  IconWallet,
  IconX,
} from "@tabler/icons-react";
import { ReferralStatus, ReferralTierState, ReferredDriverStatus } from "@safiri/shared";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import type { Paged, ReferralDetail, ReferralItem, Summary, Tier } from "../lib/types";
import { formatDate, phoneDigits, tzs } from "../lib/format";
import { DRIVER_APP_URL } from "../config/env";
import { useI18n, type TKey } from "../i18n";
import { prettyPhone } from "./AuthPages";
import { PushSettings } from "../components/Install";
import {
  Avatar,
  Badge,
  Button,
  ButtonLink,
  Card,
  Chips,
  cx,
  EmptyState,
  ErrorAlert,
  Input,
  ListSkeleton,
  PageTitle,
  Progress,
  SectionTitle,
  Select,
  Skeleton,
  Spinner,
  Stat,
  useCopy,
  useLoad,
  useToast,
} from "../components/ui";

export const driverStatusKey = (s: string) => `ds_${s}` as TKey;
export const rewardStatusKey = (s: string) => `rs_${s}` as TKey;
const tierStateKey = (s: string) => `ts_${s}` as TKey;

/** Share the code: the phone's own share sheet where there is one, else WhatsApp/SMS links. */
function useShare(code: string) {
  const { t } = useI18n();
  const message = t("shareMessage", { link: DRIVER_APP_URL, code });
  const canNative = typeof navigator !== "undefined" && typeof navigator.share === "function";
  const native = async () => {
    try {
      await navigator.share({ title: t("shareTitle"), text: message });
    } catch {
      // Cancelled by the agent.
    }
  };
  return {
    message,
    canNative,
    native,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(message)}`,
    // iOS wants "&body", Android "?body"; "?&body" works on both.
    sms: `sms:?&body=${encodeURIComponent(message)}`,
  };
}

function CodeHero({ code, compact }: { code: string; compact?: boolean }) {
  const { t } = useI18n();
  const toast = useToast();
  const { copied, copy } = useCopy();
  const share = useShare(code);
  return (
    <div className="relative overflow-hidden rounded-3xl bg-primary p-5 text-white shadow-raised sm:p-6">
      <img src="/img/pattern.png" alt="" className="pointer-events-none absolute -right-20 -top-20 w-64 opacity-[0.1] mix-blend-screen" aria-hidden />
      <div className="relative">
        <p className="text-sm text-white/80">{t("myCode")}</p>
        <div className="mt-1 flex items-center gap-3">
          <p className="select-all font-mono text-[34px] font-bold tracking-[0.18em] sm:text-4xl">{code}</p>
          <button
            type="button"
            onClick={async () => {
              await copy(code);
              toast(t("codeCopied"));
            }}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/15 transition hover:bg-white/25 active:scale-95"
            aria-label={t("copy")}
          >
            {copied ? <IconCheck size={20} /> : <IconCopy size={20} />}
          </button>
        </div>
        {!compact && <p className="mt-2 max-w-md text-sm text-white/80">{t("codeHint")}</p>}
        <div className="mt-5 grid grid-cols-2 gap-2 sm:flex">
          {share.canNative ? (
            <button type="button" onClick={() => void share.native()} className="col-span-2 inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl bg-white px-4 text-sm font-semibold text-primary transition active:scale-[.98] sm:col-span-1">
              <IconShare size={18} /> {t("shareCode")}
            </button>
          ) : null}
          <a href={share.whatsapp} target="_blank" rel="noreferrer" className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl bg-whatsapp px-4 text-sm font-semibold text-white transition active:scale-[.98]">
            <IconBrandWhatsapp size={18} /> WhatsApp
          </a>
          <a href={share.sms} className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl bg-white/15 px-4 text-sm font-semibold text-white transition hover:bg-white/25 active:scale-[.98]">
            <IconMessage size={18} /> SMS
          </a>
          {compact && (
            <Link to="/code" className="col-span-2 inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl bg-white/15 px-4 text-sm font-semibold text-white transition hover:bg-white/25 sm:col-span-1">
              <IconQrcode size={18} /> {t("showQr")}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

/** Driver statuses as one stacked bar, each segment a filter link. */
const PIPELINE: { s: ReferredDriverStatus; color: string }[] = [
  { s: ReferredDriverStatus.ACTIVE, color: "bg-success" },
  { s: ReferredDriverStatus.UNDER_REVIEW, color: "bg-info" },
  { s: ReferredDriverStatus.DOCUMENTS_PENDING, color: "bg-warning" },
  { s: ReferredDriverStatus.DOCUMENTS_EXPIRED, color: "bg-warning/60" },
  { s: ReferredDriverStatus.SUSPENDED, color: "bg-danger/70" },
  { s: ReferredDriverStatus.REJECTED, color: "bg-danger" },
  { s: ReferredDriverStatus.DELETED, color: "bg-subtle" },
];

function Pipeline({ summary }: { summary: Summary }) {
  const { t } = useI18n();
  const rows = PIPELINE.map((p) => ({ ...p, n: summary.byDriverStatus[p.s] ?? 0 })).filter((r) => r.n > 0);
  const total = rows.reduce((a, r) => a + r.n, 0);
  if (!total) return null;
  return (
    <Card>
      <h2 className="mb-4 font-semibold text-ink">{t("driverPipeline")}</h2>
      <div className="flex h-3 w-full overflow-hidden rounded-full bg-sunken">
        {rows.map((r) => (
          <span key={r.s} className={r.color} style={{ width: `${(r.n / total) * 100}%` }} />
        ))}
      </div>
      <ul className="mt-4 grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-3">
        {rows.map((r) => (
          <li key={r.s}>
            <Link to={`/referrals?driverStatus=${r.s}`} className="flex items-center gap-2 rounded-lg py-1.5 text-sm hover:text-brand">
              <span className={cx("h-2.5 w-2.5 shrink-0 rounded-full", r.color)} />
              <span className="flex-1 truncate text-muted">{t(driverStatusKey(r.s))}</span>
              <span className="tabular font-semibold text-ink">{r.n}</span>
            </Link>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function ReferralRow({ r }: { r: ReferralItem }) {
  const { t } = useI18n();
  const pct = r.max ? (r.paid / r.max) * 100 : 0;
  const callable = r.phoneNumber && r.driverStatus !== ReferredDriverStatus.DELETED;
  return (
    <li className="relative flex items-center gap-3 rounded-2xl border border-line bg-surface p-3.5 shadow-card transition hover:border-line-strong sm:p-4">
      <Avatar name={r.name} src={r.profilePicUrl} size={46} />
      <Link to={`/referrals/${r.id}`} className="min-w-0 flex-1 after:absolute after:inset-0 after:rounded-2xl">
        <div className="flex items-center gap-2">
          <p className="truncate font-semibold text-ink">{r.name}</p>
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          <Badge status={r.driverStatus}>{t(driverStatusKey(r.driverStatus))}</Badge>
          <span className="text-xs text-subtle">{t("trips", { n: r.tripCount })}</span>
        </div>
        <div className="mt-2 flex items-center gap-2">
          <div className="max-w-[160px] flex-1">
            <Progress value={pct} tone={r.status === ReferralStatus.COMPLETED ? "success" : "primary"} />
          </div>
          <span className="tabular whitespace-nowrap text-xs text-muted">{t("paidOf", { paid: tzs(r.paid), max: tzs(r.max) })}</span>
        </div>
      </Link>
      {callable ? (
        <a href={`tel:${r.phoneNumber}`} className="relative z-10 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-soft text-brand transition hover:bg-primary/15" aria-label={`${t("callDriver")} ${r.name}`}>
          <IconPhone size={19} />
        </a>
      ) : (
        <IconChevronRight size={18} className="shrink-0 text-subtle" />
      )}
    </li>
  );
}

export function HomePage() {
  const { t, lang } = useI18n();
  const { me } = useAuth();
  const s = useLoad(() => api.get<Summary>("/agent/summary"));
  const recent = useLoad(() => api.get<Paged<ReferralItem>>("/agent/referrals", { page: 1, limit: 5 }));
  const today = new Date().toLocaleDateString(lang === "sw" ? "sw-TZ" : "en-GB", { weekday: "long", day: "numeric", month: "long", timeZone: "Africa/Dar_es_Salaam" });
  const d = s.data;
  return (
    <>
      <PageTitle subtitle={today}>{t("hello", { name: me?.name.split(" ")[0] ?? "" })}</PageTitle>
      <div className="flex flex-col gap-5">
        {me?.referralCode && <CodeHero code={me.referralCode} compact />}

        {s.error ? (
          <ErrorAlert error={s.error} onRetry={s.reload} />
        ) : !d ? (
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} className="h-[104px] !rounded-2xl" />
            ))}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <Stat label={t("walletBalance")} value={tzs(d.walletBalance)} icon={IconWallet} to="/wallet" accent />
              <Stat label={t("rewardsCredited")} value={tzs(d.rewardsCredited)} icon={IconTrophy} />
              <Stat label={t("driversReferred")} value={d.total} icon={IconUsers} to="/referrals" />
              <Stat label={t("stillPossible")} value={tzs(d.rewardsStillPossible)} hint={t("stillPossibleHint")} icon={IconSparkles} />
            </div>

            <PushSettings compact />

            {d.total === 0 ? (
              <EmptyState image="/img/empty.png" title={t("firstDriverTitle")} body={t("firstDriverBody")} action={<ButtonLink to="/code">{t("shareCode")}</ButtonLink>} />
            ) : (
              <>
                <Pipeline summary={d} />
                <Card>
                  <h2 className="mb-3 font-semibold text-ink">{t("byStatus")}</h2>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    {Object.values(ReferralStatus).map((st) => (
                      <Link key={st} to={`/referrals?status=${st}`} className="rounded-xl bg-sunken p-3 transition hover:bg-line">
                        <p className="tabular text-xl font-semibold text-ink">{d.byStatus[st] ?? 0}</p>
                        <p className="mt-0.5 text-xs text-muted">{t(rewardStatusKey(st))}</p>
                      </Link>
                    ))}
                  </div>
                </Card>
              </>
            )}
          </>
        )}

        {recent.data && recent.data.items.length > 0 && (
          <section>
            <SectionTitle
              action={
                <Link to="/referrals" className="inline-flex items-center gap-1 text-sm font-medium text-brand">
                  {t("seeAll")} <IconArrowRight size={16} />
                </Link>
              }
            >
              {t("recentDrivers")}
            </SectionTitle>
            <ul className="flex flex-col gap-2.5">
              {recent.data.items.map((r) => (
                <ReferralRow key={r.id} r={r} />
              ))}
            </ul>
          </section>
        )}
      </div>
    </>
  );
}

export function CodePage() {
  const { t } = useI18n();
  const { me } = useAuth();
  const toast = useToast();
  const { copy } = useCopy();
  const [qr, setQr] = useState<string | null>(null);
  const code = me?.referralCode ?? "";
  const share = useShare(code);
  useEffect(() => {
    if (!code) return;
    // The QR carries the code itself: drivers type it at sign-up (spec §12, no auto-filled links at launch).
    // Loaded on demand: only this page needs the QR encoder.
    import("qrcode")
      .then((QRCode) => QRCode.toDataURL(code, { width: 720, margin: 1, errorCorrectionLevel: "M", color: { dark: "#292F30", light: "#FFFFFF" } }))
      .then(setQr)
      .catch(() => setQr(null));
  }, [code]);
  if (!code) return <Spinner />;
  return (
    <>
      <PageTitle subtitle={t("codePageBody")}>{t("navCode")}</PageTitle>
      <div className="flex flex-col gap-5">
        <CodeHero code={code} />

        <div className="grid gap-5 lg:grid-cols-[1fr_1fr]">
          <Card className="print-area flex flex-col items-center text-center">
            <img src="/wordmark-ink.png" alt="Smart Safiri" className="hidden h-5 w-auto print:block" />
            <h2 className="font-semibold text-ink print:mt-6 print:text-2xl">{t("qrTitle")}</h2>
            <div className="mt-4 rounded-2xl bg-white p-3 ring-1 ring-line">{qr ? <img src={qr} alt={code} className="h-56 w-56 sm:h-64 sm:w-64" /> : <Skeleton className="h-56 w-56" />}</div>
            <p className="mt-3 font-mono text-2xl font-bold tracking-[0.2em] text-ink">{code}</p>
            <p className="mt-1 max-w-xs text-sm text-muted">{t("codeHint")}</p>
            <div className="no-print mt-5 grid w-full grid-cols-2 gap-2">
              <Button variant="secondary" onClick={() => window.print()}>
                <IconPrinter size={18} /> {t("printQr")}
              </Button>
              {qr ? (
                <a href={qr} download={`safiri-agent-${code}.png`} className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl bg-surface px-4 text-sm font-semibold text-ink ring-1 ring-inset ring-line-strong hover:bg-sunken">
                  <IconDownload size={18} /> {t("downloadQr")}
                </a>
              ) : (
                <span />
              )}
            </div>
          </Card>

          <Card className="no-print">
            <h2 className="font-semibold text-ink">{t("shareMessageTitle")}</h2>
            <p className="mt-3 whitespace-pre-line rounded-xl bg-sunken p-4 text-sm leading-relaxed text-ink">{share.message}</p>
            <Button
              variant="secondary"
              block
              className="mt-3"
              onClick={async () => {
                await copy(share.message);
                toast(t("messageCopied"));
              }}
            >
              <IconCopy size={18} /> {t("copyMessage")}
            </Button>
            <h3 className="mb-3 mt-6 text-sm font-semibold text-ink">{t("howItWorks")}</h3>
            <ol className="flex flex-col gap-3">
              {(["how1", "how2", "how3"] as TKey[]).map((k, i) => (
                <li key={k} className="flex gap-3 text-sm text-muted">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-soft text-xs font-bold text-brand">{i + 1}</span>
                  {t(k)}
                </li>
              ))}
            </ol>
          </Card>
        </div>
      </div>
    </>
  );
}

const PAGE_SIZE = 20;

export function ReferralsPage() {
  const { t } = useI18n();
  // Filters live in the URL so Home's links, back/forward and refresh keep them.
  const [params, setParams] = useSearchParams();
  const status = params.get("status") ?? "";
  const driverStatus = params.get("driverStatus") ?? "";
  const page = Math.max(1, Number(params.get("page") ?? 1) || 1);
  const [search, setSearch] = useState(params.get("q") ?? "");
  const [debounced, setDebounced] = useState(search);

  const update = (next: Record<string, string | number | null>) =>
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(next)) {
          if (v === null || v === "" || (k === "page" && v === 1)) p.delete(k);
          else p.set(k, String(v));
        }
        return p;
      },
      { replace: true }
    );

  useEffect(() => {
    const id = setTimeout(() => {
      setDebounced(search);
      setParams(
        (prev) => {
          if ((prev.get("q") ?? "") === search) return prev;
          const p = new URLSearchParams(prev);
          if (search) p.set("q", search);
          else p.delete("q");
          p.delete("page");
          return p;
        },
        { replace: true }
      );
    }, 300);
    return () => clearTimeout(id);
    // Only the search box drives this; `update` reads the latest params itself.
  }, [search]);

  const summary = useLoad(() => api.get<Summary>("/agent/summary"));
  const list = useLoad(
    () => api.get<Paged<ReferralItem>>("/agent/referrals", { search: debounced || undefined, status: status || undefined, driverStatus: driverStatus || undefined, page, limit: PAGE_SIZE }),
    [debounced, status, driverStatus, page]
  );
  const statusOptions = useMemo(
    () => [
      { value: "", label: t("all"), count: summary.data?.total },
      ...Object.values(ReferralStatus).map((s) => ({ value: s as string, label: t(rewardStatusKey(s)), count: summary.data?.byStatus[s] ?? 0 })),
    ],
    [t, summary.data]
  );
  const filtered = Boolean(debounced || status || driverStatus);
  const pg = list.data?.pagination;

  return (
    <>
      <PageTitle subtitle={summary.data ? t("driversCount", { n: summary.data.total }) : undefined}>{t("navDrivers")}</PageTitle>
      <div className="mb-4 flex flex-col gap-3">
        <div className="grid gap-3 sm:grid-cols-[1fr_220px]">
          <Input
            label={t("searchDrivers")}
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leading={<IconSearch size={18} />}
            trailing={
              search ? (
                <button type="button" className="inline-flex h-9 w-9 items-center justify-center rounded-full text-muted hover:bg-sunken" onClick={() => setSearch("")} aria-label={t("clear")}>
                  <IconX size={16} />
                </button>
              ) : undefined
            }
            placeholder={t("searchPlaceholder")}
            className="!pl-11"
          />
          <Select
            label={t("driverStatus")}
            value={driverStatus}
            placeholder={t("allDriverStatuses")}
            onChange={(e) => update({ driverStatus: e.target.value, page: null })}
            options={Object.values(ReferredDriverStatus).map((s) => ({ value: s, label: t(driverStatusKey(s)) }))}
          />
        </div>
        <Chips label={t("rewardProgress")} value={status} onChange={(v) => update({ status: v, page: null })} options={statusOptions} />
      </div>

      {list.error ? (
        <ErrorAlert error={list.error} onRetry={list.reload} />
      ) : !list.data ? (
        <ListSkeleton rows={5} />
      ) : !list.data.items.length ? (
        filtered ? (
          <EmptyState
            icon={IconSearch}
            title={t("noMatches")}
            body={t("noMatchesBody")}
            action={
              <Button
                variant="secondary"
                onClick={() => {
                  setSearch("");
                  setParams(new URLSearchParams(), { replace: true });
                }}
              >
                {t("clearFilters")}
              </Button>
            }
          />
        ) : (
          <EmptyState image="/img/empty.png" title={t("firstDriverTitle")} body={t("noReferrals")} action={<ButtonLink to="/code">{t("shareCode")}</ButtonLink>} />
        )
      ) : (
        <>
          <ul className={cx("flex flex-col gap-2.5 transition-opacity", list.loading && "opacity-60")}>
            {list.data.items.map((r) => (
              <ReferralRow key={r.id} r={r} />
            ))}
          </ul>
          {pg && pg.pages > 1 && (
            <nav className="mt-5 flex items-center justify-between gap-3" aria-label={t("pagination")}>
              <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => update({ page: page - 1 })}>
                <IconChevronLeft size={16} /> {t("previous")}
              </Button>
              <span className="tabular text-sm text-muted">{t("pageOf", { page, pages: pg.pages })}</span>
              <Button variant="secondary" size="sm" disabled={page >= pg.pages} onClick={() => update({ page: page + 1 })}>
                {t("next")} <IconChevronRight size={16} />
              </Button>
            </nav>
          )}
        </>
      )}
    </>
  );
}

const TIER_ICON: Record<string, { icon: typeof IconCheck; cls: string }> = {
  [ReferralTierState.PAID]: { icon: IconCircleCheck, cls: "bg-success text-white dark:text-canvas" },
  [ReferralTierState.LOCKED]: { icon: IconLock, cls: "bg-sunken text-muted ring-1 ring-line-strong" },
  expired: { icon: IconHourglass, cls: "bg-sunken text-subtle" },
  cancelled: { icon: IconCircleX, cls: "bg-sunken text-subtle" },
  reversed: { icon: IconCircleX, cls: "bg-danger-soft text-danger" },
};

function TierStep({ tier, index, last }: { tier: Tier; index: number; last: boolean }) {
  const { t, pick, lang } = useI18n();
  const st = TIER_ICON[tier.state] ?? TIER_ICON[ReferralTierState.LOCKED];
  const urgent = tier.daysRemaining != null && tier.daysRemaining <= 3;
  return (
    <li className="relative flex gap-4 pb-6 last:pb-0">
      {!last && <span className={cx("absolute left-[17px] top-10 h-[calc(100%-2.5rem)] w-0.5", tier.state === ReferralTierState.PAID ? "bg-success/40" : "bg-line-strong")} aria-hidden />}
      <span className={cx("relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full", st.cls)}>
        <st.icon size={18} />
      </span>
      <div className="min-w-0 flex-1 pt-0.5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-medium uppercase tracking-wide text-subtle">
              {t("milestone")} {index + 1}
            </p>
            <p className="font-semibold text-ink first-letter:uppercase">{pick(tier.label)}</p>
          </div>
          <p className={cx("tabular whitespace-nowrap font-semibold", tier.state === ReferralTierState.PAID ? "text-success" : "text-ink")}>{tzs(tier.amount)}</p>
        </div>
        <div className="mt-1.5 flex flex-wrap items-center gap-2 text-sm text-muted">
          <Badge status={tier.state}>{t(tierStateKey(tier.state))}</Badge>
          <span>
            {tier.paidAt
              ? t("paidOn", { date: formatDate(tier.paidAt, lang) })
              : tier.deadline
                ? `${t("deadline")}: ${formatDate(tier.deadline, lang)}`
                : t("startsAfter", { days: tier.windowDays, anchor: t(`anchor_${tier.windowFrom}` as TKey) })}
          </span>
        </div>
        {tier.daysRemaining != null && tier.state === ReferralTierState.LOCKED && (
          <p className={cx("mt-2 inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-semibold", urgent ? "bg-danger-soft text-danger" : "bg-sunken text-ink")}>
            <IconClock size={14} />
            {t("daysLeft", { n: tier.daysRemaining })}
          </p>
        )}
      </div>
    </li>
  );
}

export function ReferralDetailPage() {
  const { t, lang } = useI18n();
  const { id = "" } = useParams();
  const r = useLoad(() => api.get<ReferralDetail>(`/agent/referrals/${id}`), [id]);
  if (r.loading && !r.data)
    return (
      <>
        <PageTitle back="/referrals">{t("referralDetail")}</PageTitle>
        <ListSkeleton rows={3} />
      </>
    );
  if (r.error || !r.data)
    return (
      <>
        <PageTitle back="/referrals">{t("referralDetail")}</PageTitle>
        <ErrorAlert error={r.error} onRetry={r.reload} />
      </>
    );
  const d = r.data;
  const pct = d.progress.max ? (d.progress.paid / d.progress.max) * 100 : 0;
  const reachable = d.phoneNumber && d.driverStatus !== ReferredDriverStatus.DELETED;
  const digits = phoneDigits(d.phoneNumber ?? "");
  const facts = [
    { label: t("signedUpLabel"), value: formatDate(d.signedUpAt, lang) },
    { label: t("activatedLabel"), value: formatDate(d.activatedAt, lang) },
    { label: t("firstTripLabel"), value: formatDate(d.firstTripAt, lang) },
    { label: t("lastTripLabel"), value: formatDate(d.lastTripAt, lang) },
  ];
  return (
    <>
      <PageTitle back="/referrals">{t("referralDetail")}</PageTitle>
      <div className="flex flex-col gap-4">
        <Card>
          <div className="flex items-center gap-4">
            <Avatar name={d.name} src={d.profilePicUrl} size={64} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-lg font-semibold text-ink">{d.name}</p>
              {d.phoneNumber && <p className="text-sm text-muted">{prettyPhone(d.phoneNumber)}</p>}
              <div className="mt-2 flex flex-wrap gap-1.5">
                <Badge status={d.driverStatus}>{t(driverStatusKey(d.driverStatus))}</Badge>
                <Badge status={d.status}>{t(rewardStatusKey(d.status))}</Badge>
              </div>
            </div>
          </div>
          {reachable && (
            <div className="mt-4 grid grid-cols-3 gap-2">
              <a href={`tel:${d.phoneNumber}`} className="inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-xl bg-primary px-3 text-sm font-semibold text-white">
                <IconPhone size={18} /> {t("callDriver")}
              </a>
              <a href={`https://wa.me/${digits}`} target="_blank" rel="noreferrer" className="inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-xl bg-whatsapp px-3 text-sm font-semibold text-white">
                <IconBrandWhatsapp size={18} /> WhatsApp
              </a>
              <a href={`sms:${d.phoneNumber}`} className="inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-xl bg-surface px-3 text-sm font-semibold text-ink ring-1 ring-inset ring-line-strong">
                <IconMessage size={18} /> SMS
              </a>
            </div>
          )}
          {d.driverStatus === ReferredDriverStatus.DELETED && <p className="mt-3 text-sm text-muted">{t("driverDeletedNote")}</p>}
          <dl className="mt-4 grid grid-cols-2 gap-3 border-t border-line pt-4 sm:grid-cols-4">
            {facts.map((f) => (
              <div key={f.label}>
                <dt className="text-xs text-subtle">{f.label}</dt>
                <dd className="mt-0.5 text-sm font-medium text-ink">{f.value}</dd>
              </div>
            ))}
          </dl>
        </Card>

        <Card>
          <div className="flex items-end justify-between gap-3">
            <div>
              <p className="text-sm text-muted">{t("rewardProgress")}</p>
              <p className="tabular mt-1 text-2xl font-semibold text-ink">
                {tzs(d.progress.paid)} <span className="text-base font-normal text-subtle">/ {tzs(d.progress.max)}</span>
              </p>
            </div>
            <p className="tabular text-sm font-semibold text-brand">{Math.round(pct)}%</p>
          </div>
          <div className="mt-3">
            <Progress value={pct} tone={d.status === ReferralStatus.COMPLETED ? "success" : "primary"} label={t("rewardProgress")} />
          </div>
          {d.progress.possible > 0 && (
            <p className="mt-3 flex items-center gap-1.5 text-sm text-muted">
              <IconCash size={16} className="text-success" />
              {t("stillToEarn", { amount: tzs(d.progress.possible) })}
            </p>
          )}
          <p className="mt-1 text-sm text-muted">{t("trips", { n: d.tripCount })}</p>
        </Card>

        <Card>
          <h2 className="mb-5 font-semibold text-ink">{t("milestones")}</h2>
          <ol>
            {d.tiers.map((tier, i) => (
              <TierStep key={tier.key} tier={tier} index={i} last={i === d.tiers.length - 1} />
            ))}
          </ol>
        </Card>
      </div>
    </>
  );
}
